// "Ik loop achter" (spec V5): a proposal for the rest of today. Nothing changes until PK confirms (AC3).

import { blockEnds, sortBlocks, type BlockLike } from "./rhythm";
import { timeToMinutes } from "./time";

export interface LateBlock extends BlockLike {
  is_fixed: boolean;
  status: "open" | "done" | "skipped";
}

/** Something that never moves: a fixed block (work, bedtime) or an appointment (V5 AC2). */
export interface Anchor {
  title: string;
  start: number;
}

export interface Shift<T> {
  block: T;
  from: number;
  to: number;
}

export interface LatePlan<T> {
  /** Open, flexible blocks that are not over yet, up to the first thing that never moves. */
  movable: T[];
  /** Optional blocks and steps of a group among them, which can be dropped instead (AC1). */
  droppable: T[];
  /** The first fixed block or appointment after now: blocks from there on stay where they are. */
  anchor: Anchor | null;
}

const movableKind = (b: LateBlock) => !b.is_fixed && b.slot_kind !== "work";

export function latePlan<T extends LateBlock>(blocks: T[], appointments: Anchor[], minute: number): LatePlan<T> {
  const sorted = sortBlocks(blocks);
  const ends = blockEnds(sorted);
  const anchors: Anchor[] = [
    ...sorted.filter((b) => !movableKind(b)).map((b) => ({ title: b.title, start: timeToMinutes(b.start_local) })),
    ...appointments,
  ]
    .filter((a) => a.start > minute)
    .sort((a, b) => a.start - b.start);
  const anchor = anchors[0] ?? null;
  const movable = sorted.filter(
    (b, i) => movableKind(b) && b.status === "open" && (ends[i] ?? 0) > minute && (!anchor || timeToMinutes(b.start_local) < anchor.start),
  );
  return { movable, droppable: movable.filter((b) => b.is_optional || !!b.group_title), anchor };
}

/** The new times when the movable blocks go `minutes` later; a block that would pass midnight stops at 23:59. */
export function shifted<T extends LateBlock>(movable: T[], minutes: number): Shift<T>[] {
  return movable.map((block) => {
    const from = timeToMinutes(block.start_local);
    return { block, from, to: Math.min(from + minutes, 23 * 60 + 59) };
  });
}

/**
 * The block that would run into the anchor once moved, if any: it would start at or after the anchor, or its own
 * end would pass it. The proposal then says so; the anchor itself never moves.
 */
export function overrun<T extends LateBlock>(movable: T[], minutes: number, anchor: Anchor | null): T | null {
  if (!anchor) return null;
  return (
    movable.find((b) => {
      const start = timeToMinutes(b.start_local) + minutes;
      const end = b.end_local ? timeToMinutes(b.end_local) + minutes : start;
      return start >= anchor.start || end > anchor.start;
    }) ?? null
  );
}
